import { Button, VStack, Text, Flex, List } from '@chakra-ui/react';
import { SchoolUserRole } from '@novaheal/types';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ModalRoleConfirmationVariantProps, SharedModalProps } from 'types/Modal';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalRoleConfirmationVariantProps;

const ROLE_LABELS: Record<
  SchoolUserRole.TEACHER | SchoolUserRole.STUDENT | SchoolUserRole.GUIDE,
  string
> = {
  [SchoolUserRole.TEACHER]: 'Lehrperson',
  [SchoolUserRole.STUDENT]: 'Lernende:r',
  [SchoolUserRole.GUIDE]: 'Praxisanleitung',
};

const teacherDescription = (
  <Flex direction="column" gap="xs">
    <Text>
      Wenn du diese Rolle änderst, verliert der Benutzer den Zugriff auf Funktionen auf Lehrerebene:
    </Text>
    <List.Root as="ul">
      <List.Item marginLeft="18px">Sichtbarkeit aller zugewiesenen Kurse der Einrichtung</List.Item>
      <List.Item marginLeft="18px">Entfernung aus allen derzeit zugewiesenen Kurse</List.Item>
      <List.Item marginLeft="18px">Möglichkeit, Arbeitsblätter Kursen zuzuweisen</List.Item>
    </List.Root>
  </Flex>
);

const studentDescription = (
  <Flex direction="column" gap="xs">
    <Text>
      Wenn du diese Rolle änderst, verliert der Benutzer den Zugriff auf Funktionen, die an die
      Rolle der Lernenden gebunden sind:
    </Text>
    <List.Root as="ul">
      <List.Item marginLeft="18px">Aus allen zugewiesenen Kursen entfernt</List.Item>
    </List.Root>
  </Flex>
);

const guideDescription = (
  <Flex direction="column" gap="xs">
    <Text>
      Wenn du diese Rolle änderst, verliert der Benutzer den Zugriff auf Funktionen auf
      Praxisanleitungsebene:
    </Text>
    <List.Root as="ul">
      <List.Item marginLeft="18px">
        Entfernung aus allen derzeit zugewiesenen Kursen als Praxisanleitung
      </List.Item>
    </List.Root>
  </Flex>
);

const getRoleChangeCopy = (
  newRole: SchoolUserRole,
  currentRole: SchoolUserRole
): { titleRole: string; description: JSX.Element } => {
  const titleRole = ROLE_LABELS[newRole as keyof typeof ROLE_LABELS] ?? newRole;

  if (newRole === SchoolUserRole.STUDENT) {
    if (currentRole === SchoolUserRole.GUIDE) {
      return { titleRole, description: guideDescription };
    }
    return { titleRole, description: teacherDescription };
  }

  if (newRole === SchoolUserRole.TEACHER) {
    if (currentRole === SchoolUserRole.GUIDE) {
      return { titleRole, description: guideDescription };
    }
    return { titleRole, description: studentDescription };
  }

  if (newRole === SchoolUserRole.GUIDE) {
    if (currentRole === SchoolUserRole.TEACHER) {
      return { titleRole, description: teacherDescription };
    }
    return { titleRole, description: studentDescription };
  }

  return { titleRole, description: studentDescription };
};

export const UserClassRoleChangeModal = ({
  handleClose,
  isShown,
  onConfirm,
  firstName,
  lastName,
  newRole,
  currentRole,
  memberOfClasses,
  teacherOfClasses,
  guideOfClasses,
}: Props): JSX.Element => {
  const { titleRole, description } = getRoleChangeCopy(newRole, currentRole);

  const classes = [
    ...(memberOfClasses ?? []),
    ...(teacherOfClasses ?? []),
    ...(guideOfClasses ?? []),
  ];

  const handleConfirm = (): void => {
    if (onConfirm) {
      onConfirm();
    }
    handleClose();
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" hideHeader size="md">
      <CustomModalContent p="30px">
        <Flex direction="column" gap="30px">
          <Flex direction="column" align="flex-end" gap="5px">
            <NovaIconButton
              aria-label="Schließen"
              variant="primary"
              size="l"
              onClick={handleClose}
              minW="24px"
              minH="24px"
              p="0"
              icon={CloseIcon}
              color="icon.secondary"
            />
            <VStack gap="15px" align="flex-start" w="full">
              <Text textStyle="h4.medium" color="text.primary">
                Ändere die Rolle von „{firstName} {lastName}” in {titleRole}.
              </Text>
              {description}
              <Text textStyle="body.m.regular" color="text.primary">
                Zugewiesene Kurse:
              </Text>
              <List.Root as="ul">
                {classes.map((c) => (
                  <List.Item key={c.id} marginLeft="18px">
                    {c.name}
                  </List.Item>
                ))}
              </List.Root>
            </VStack>
          </Flex>

          <Flex gap="l" justify="flex-end">
            <Button onClick={handleClose} variant="tertiary">
              <Text textStyle="body.m.regular" color="text.secondary">
                Abbrechen
              </Text>
            </Button>
            <Button onClick={handleConfirm} variant="destructive">
              <Text textStyle="body.m.regular" color="grey.white">
                Bestätigen
              </Text>
            </Button>
          </Flex>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
